import React, { useEffect, useMemo, useState } from 'react';
import { Button, Label } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { TICKETING_REPORT_DATE_RANGE_OPTIONS } from '../reportScheduleConstants';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2019 }, (_, i) => CURRENT_YEAR - i);

/**
 * Catalog of schedulable report pages. Paths use `{frontendBaseUrl}` so Application
 * Settings fill the host when the schedule runs.
 */
export const SCHEDULED_REPORT_CATALOG = [
  {
    id: 'complaint',
    label: 'Complaints',
    group: 'Reports & Analytics',
    path: '/reports/complaint',
    needsYear: true,
  },
  {
    id: 'risk-assessment',
    label: 'Risk Assessments',
    group: 'Reports & Analytics',
    path: '/reports/risk-assessment',
    needsYear: true,
  },
  {
    id: 'deviation',
    label: 'Deviations',
    group: 'Reports & Analytics',
    path: '/reports/deviation',
    needsYear: true,
  },
  {
    id: 'product-status-change',
    label: 'Product Status Change',
    group: 'Reports & Analytics',
    path: '/reports/product-status-change',
    needsYear: true,
  },
  {
    id: 'todo',
    label: 'Todos',
    group: 'Reports & Analytics',
    path: '/reports/todo',
    needsYear: true,
  },
  {
    id: 'ticketing-year',
    label: 'Ticketing (by year)',
    group: 'Reports & Analytics',
    path: '/reports/ticketing',
    needsYear: true,
  },
  {
    id: 'manuals',
    label: 'Manuals',
    group: 'Reports & Analytics',
    path: '/reports/manuals',
    needsYear: true,
  },
  {
    id: 'contracts',
    label: 'Contracts',
    group: 'Reports & Analytics',
    path: '/reports/contracts',
    needsYear: true,
  },
  {
    id: 'open-todos',
    label: 'Open Todos (current backlog)',
    group: 'Report Scheduling',
    path: '/report-scheduling/open-todos',
    needsYear: false,
  },
  {
    id: 'ticketing-status',
    label: 'Ticketing Status Analytics',
    group: 'Report Scheduling',
    path: '/report-scheduling/ticketing-status-analytics',
    needsYear: false,
    needsRange: true,
  },
  {
    id: 'custom',
    label: 'Custom URL…',
    group: 'Other',
    path: null,
    needsYear: false,
  },
];

function buildReportUrl(entry, { year, range }) {
  if (!entry?.path) return '';
  let url = `{frontendBaseUrl}${entry.path}`;
  if (entry.needsYear) {
    url += `?year=${encodeURIComponent(year || CURRENT_YEAR)}`;
  } else if (entry.needsRange) {
    url += `?range=${encodeURIComponent(range || 'this_month')}`;
  }
  return url;
}

function normalizeStoredUrl(raw) {
  return String(raw || '')
    .trim()
    .replace(/\/+$/, '');
}

/** Match a stored URL back to a catalog entry + year/range. */
export function detectCatalogFromUrl(raw) {
  const value = normalizeStoredUrl(raw);
  if (!value) {
    return {
      id: 'complaint',
      year: CURRENT_YEAR,
      range: 'this_month',
      custom: false,
    };
  }

  let pathPart = value;
  let query = '';
  try {
    if (/^https?:\/\//i.test(value)) {
      const u = new URL(value);
      pathPart = u.pathname;
      query = u.search;
    } else if (value.startsWith('{frontendBaseUrl}')) {
      const rest = value.slice('{frontendBaseUrl}'.length);
      const q = rest.indexOf('?');
      pathPart = q >= 0 ? rest.slice(0, q) : rest;
      query = q >= 0 ? rest.slice(q) : '';
    } else if (value.startsWith('/')) {
      const q = value.indexOf('?');
      pathPart = q >= 0 ? value.slice(0, q) : value;
      query = q >= 0 ? value.slice(q) : '';
    }
  } catch {
    /* keep raw */
  }

  pathPart = pathPart.replace(/\/+$/, '').toLowerCase();
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  const year = Number(params.get('year')) || CURRENT_YEAR;
  const range = params.get('range') || 'this_month';

  for (const entry of SCHEDULED_REPORT_CATALOG) {
    if (!entry.path) continue;
    const seg = entry.path.toLowerCase();
    if (pathPart === seg || pathPart.endsWith(seg)) {
      return { id: entry.id, year, range, custom: false };
    }
  }

  return { id: 'custom', year: CURRENT_YEAR, range: 'this_month', custom: true };
}

/**
 * Resolve a stored schedule URL for "Open report".
 * Prefer same-origin relative paths so the SPA opens in this tab's host
 * (Application Settings frontendBaseUrl may point at another host/port).
 */
function resolveOpenableUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';

  if (value.includes('{frontendBaseUrl}')) {
    const rest = value.replace(/\{frontendBaseUrl\}/gi, '');
    if (rest.startsWith('/')) return rest;
    return `/${rest.replace(/^\/+/, '')}`;
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const u = new URL(value);
      const path = `${u.pathname}${u.search}${u.hash}`;
      if (
        u.pathname.startsWith('/reports') ||
        u.pathname.startsWith('/report-scheduling')
      ) {
        return path;
      }
      return value;
    } catch {
      return value;
    }
  }

  if (value.startsWith('/')) return value;
  return value;
}

/**
 * Report picker for Report Schedule add/edit: choose report by name, auto-fills reportUrl.
 */
const ScheduledReportPickerField = ({ value, onChange }) => {
  const { theme } = useTheme();
  const detected = useMemo(() => detectCatalogFromUrl(value), [value]);
  const [reportId, setReportId] = useState(detected.id);
  const [year, setYear] = useState(detected.year);
  const [range, setRange] = useState(detected.range);
  const [customText, setCustomText] = useState(
    detected.custom ? String(value || '') : '',
  );

  const entry = SCHEDULED_REPORT_CATALOG.find((e) => e.id === reportId) || SCHEDULED_REPORT_CATALOG[0];

  // New form: seed a default report + link so the field is never blank.
  useEffect(() => {
    if (value != null && String(value).trim() !== '') return;
    const defaultEntry = SCHEDULED_REPORT_CATALOG.find((e) => e.id === 'complaint');
    const next = buildReportUrl(defaultEntry, {
      year: CURRENT_YEAR,
      range: 'this_month',
    });
    onChange?.(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once for empty create forms
  }, []);

  // Sync UI when form loads a different stored URL (edit mode).
  useEffect(() => {
    const next = detectCatalogFromUrl(value);
    setReportId(next.id);
    setYear(next.year);
    setRange(next.range);
    if (next.custom) setCustomText(String(value || ''));
  }, [value]);

  const emitUrl = (nextEntry, nextYear, nextRange, nextCustom) => {
    if (!nextEntry || nextEntry.id === 'custom') {
      onChange?.(nextCustom ?? '');
      return;
    }
    onChange?.(buildReportUrl(nextEntry, { year: nextYear, range: nextRange }));
  };

  const previewUrl =
    entry.id === 'custom'
      ? customText
      : buildReportUrl(entry, { year, range });

  const openableUrl = resolveOpenableUrl(previewUrl);

  const selectClass = cn(
    'h-10 w-full rounded-md border px-3 text-sm',
    theme.colors.surface,
    theme.text.primary,
    theme.border.light,
    'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
  );

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1.5 block">Report</Label>
        <select
          className={selectClass}
          value={reportId}
          onChange={(e) => {
            const nextId = e.target.value;
            const nextEntry =
              SCHEDULED_REPORT_CATALOG.find((x) => x.id === nextId) ||
              SCHEDULED_REPORT_CATALOG[SCHEDULED_REPORT_CATALOG.length - 1];
            setReportId(nextId);
            if (nextId === 'custom') {
              const seeded = value && String(value).trim() ? String(value) : '';
              setCustomText(seeded);
              emitUrl(nextEntry, year, range, seeded);
            } else {
              emitUrl(nextEntry, year, range);
            }
          }}
        >
          {['Reports & Analytics', 'Report Scheduling', 'Other'].map((group) => (
            <optgroup key={group} label={group}>
              {SCHEDULED_REPORT_CATALOG.filter((e) => e.group === group).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <p className={cn('mt-1 text-xs', theme.text.muted)}>
          Pick the report by name. The link below is filled automatically.
        </p>
      </div>

      {entry.needsYear ? (
        <div>
          <Label className="mb-1.5 block">Year</Label>
          <select
            className={cn(selectClass, 'max-w-[10rem]')}
            value={String(year)}
            onChange={(e) => {
              const y = Number(e.target.value);
              setYear(y);
              emitUrl(entry, y, range);
            }}
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {entry.needsRange ? (
        <div>
          <Label className="mb-1.5 block">Period</Label>
          <select
            className={cn(selectClass, 'max-w-[14rem]')}
            value={range}
            onChange={(e) => {
              const r = e.target.value;
              setRange(r);
              emitUrl(entry, year, r);
            }}
          >
            {TICKETING_REPORT_DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <Label className="block">Report page URL</Label>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!openableUrl}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!openableUrl) return;
              // Prefer <a>-style navigation via window.open without feature string
              // (feature strings can trigger popup blockers / odd chrome).
              const win = window.open(openableUrl, '_blank');
              if (win) win.opener = null;
            }}
          >
            Open report
          </Button>
        </div>
        {entry.id === 'custom' ? (
          <textarea
            rows={3}
            value={customText}
            onChange={(e) => {
              const t = e.target.value;
              setCustomText(t);
              emitUrl(entry, year, range, t);
            }}
            placeholder="{frontendBaseUrl}/reports/complaint?year=2026"
            className={cn(
              'w-full rounded-md border px-3 py-2 text-sm font-mono',
              theme.colors.surface,
              theme.text.primary,
              theme.border.light,
              'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
            )}
          />
        ) : (
          <div
            className={cn(
              'rounded-md border px-3 py-2.5 text-sm font-mono break-all',
              theme.border.light,
              'bg-slate-50 dark:bg-slate-900/40',
              theme.text.secondary,
            )}
          >
            {previewUrl || '—'}
          </div>
        )}
        <p className={cn('mt-1 text-xs', theme.text.muted)}>
          {entry.id === 'custom'
            ? 'Paste any path or full URL. Use {frontendBaseUrl} for the app root from Application Settings.'
            : `${entry.group} · ${entry.label}. {frontendBaseUrl} is replaced when the schedule runs.`}
        </p>
      </div>
    </div>
  );
};

export default ScheduledReportPickerField;
